// src/design-system/components/richTextEditor/extensions.ts
import { Extension } from "@tiptap/core";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { TextAlign } from "@tiptap/extension-text-align";
import { Link } from "@tiptap/extension-link";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { Placeholder } from "@tiptap/extension-placeholder";
import { CharacterCount } from "@tiptap/extension-character-count";
import { CodeBlockLowlight } from "@tiptap/extension-code-block-lowlight";
import { TaskList } from "@tiptap/extension-task-list";
import { TaskItem } from "@tiptap/extension-task-item";
import { FontFamily } from "@tiptap/extension-font-family";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { common, createLowlight } from "lowlight";
import { ResizableMedia } from "./extensions/ResizableMedia";
import { ResizableMediaComponent } from "./extensions/ResizableMediaComponent";
import { Mention, type MentionUser } from "./extensions/Mention";
import { MentionComponent } from "./extensions/MentionComponent";
import { createMentionSuggestion } from "./extensions/mentionSuggestion";

const lowlight = createLowlight(common);

// Pre-configured extensions (created once at module load time)
// This prevents React Strict Mode from seeing "duplicate" extensions
const configuredUnderline = Underline.configure({});
const configuredLink = Link.configure({
  openOnClick: false,
  autolink: true,
  defaultProtocol: "https",
  HTMLAttributes: {
    rel: "noopener noreferrer",
    class: "text-primary underline hover:text-primaryHover",
  },
});
const configuredTextStyle = TextStyle.configure({});
const configuredColor = Color.configure({});
const configuredHighlight = Highlight.configure({ multicolor: true });
const configuredFontFamily = FontFamily.configure({ types: ["textStyle"] });
const configuredSubscript = Subscript.configure({});
const configuredSuperscript = Superscript.configure({});
const configuredTextAlign = TextAlign.configure({
  types: ["heading", "paragraph"],
  alignments: ["left", "center", "right", "justify"],
});
const configuredTaskList = TaskList.configure({});
const configuredTaskItem = TaskItem.configure({ nested: true });
const configuredTable = Table.configure({
  resizable: true,
  HTMLAttributes: { class: "border-collapse table-auto w-full" },
});
const configuredTableRow = TableRow.configure({});
const configuredTableHeader = TableHeader.configure({
  HTMLAttributes: {
    class:
      "border border-surfaceSubtle bg-surfaceLighter p-2 text-left font-semibold",
  },
});
const configuredTableCell = TableCell.configure({
  HTMLAttributes: { class: "border border-surfaceSubtle p-2" },
});
const configuredCodeBlockLowlight = CodeBlockLowlight.configure({
  lowlight,
  HTMLAttributes: {
    class: "bg-surfaceLighter rounded-lg p-4 font-mono text-sm overflow-x-auto",
  },
});

// Custom FontSize extension
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (size: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
  }
}

export const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) =>
              element.style.fontSize?.replace(/['"]+/g, ""),
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) => {
          return chain().setMark("textStyle", { fontSize }).run();
        },
      unsetFontSize:
        () =>
        ({ chain }) => {
          return chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});

export interface ExtensionOptions {
  placeholder?: string;
  maxLength?: number;
  /** Upload function for media files */
  uploadFn?: (file: File) => Promise<string>;
  /** Callback when media is deleted from the editor */
  onMediaDelete?: (src: string) => void;
  /** Users available for @mention */
  mentionUsers?: MentionUser[];
}

export const getExtensions = (options: ExtensionOptions = {}) => {
  const {
    placeholder = "Start writing...",
    maxLength,
    uploadFn,
    onMediaDelete,
    mentionUsers = [],
  } = options;

  const extensions: any[] = [
    // Core - StarterKit includes most basic extensions
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3, 4, 5, 6],
      },
      codeBlock: false, // We use CodeBlockLowlight instead
      dropcursor: {
        color: "#003D8A",
        width: 2,
      },
    }),

    // Text formatting - use cached instances
    configuredUnderline,
    configuredTextStyle,
    configuredColor,
    configuredHighlight,
    configuredFontFamily,
    FontSize.configure({}),
    configuredSubscript,
    configuredSuperscript,

    // Layout & alignment
    configuredTextAlign,

    // Lists
    configuredTaskList,
    configuredTaskItem,

    // Links - use cached instance
    configuredLink,

    // Media - Resizable images, videos, and documents
    // This one needs uploadFn so we configure it dynamically
    ResizableMedia.configure({
      HTMLAttributes: {
        class: "max-w-full h-auto rounded",
      },
      uploadFn,
      onMediaDelete,
      component: ResizableMediaComponent,
    }),

    // Tables - use cached instances
    configuredTable,
    configuredTableRow,
    configuredTableHeader,
    configuredTableCell,

    // Code blocks with syntax highlighting - use cached instance
    configuredCodeBlockLowlight,

    // Utilities - these need dynamic options
    Placeholder.configure({
      placeholder,
      emptyEditorClass: "is-editor-empty",
    }),

    // Character count - needs dynamic maxLength
    CharacterCount.configure(maxLength ? { limit: maxLength } : {}),
  ];

  // Add Mention extension if users are provided
  if (mentionUsers.length > 0) {
    extensions.push(
      Mention.configure({
        HTMLAttributes: {
          class: "user-mention",
        },
        suggestion: createMentionSuggestion({ users: mentionUsers }),
        component: MentionComponent,
      }),
    );
  }

  return extensions;
};

export {
  StarterKit,
  Underline,
  TextStyle,
  Color,
  Highlight,
  TextAlign,
  ResizableMedia,
  Link,
  Table,
  TableRow,
  TableCell,
  TableHeader,
  Placeholder,
  CharacterCount,
  CodeBlockLowlight,
  TaskList,
  TaskItem,
  FontFamily,
  Subscript,
  Superscript,
  Mention,
};

export type { MentionUser };
